import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useEventStream } from '../hooks/useEventStream';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Session duration in milliseconds (24 hours)
const SESSION_DURATION_MS = 24 * 60 * 60 * 1000;

// Global axios interceptor: inject Bearer token on every request
// (skip if Authorization header already set, e.g. on login).
// AuthContext is imported once via AuthProvider, so this registers exactly once.
axios.interceptors.request.use((config) => {
  const storedToken = localStorage.getItem('token');
  if (storedToken && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${storedToken}`;
  }
  return config;
});

// Global axios response interceptor: flatten FastAPI validation error `detail`
// (which can be an array of `{type, loc, msg, input, url}` objects) into a
// plain string so consumers can safely render `error.response.data.detail`.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const detail = error?.response?.data?.detail;
    if (detail && typeof detail !== 'string') {
      if (Array.isArray(detail)) {
        error.response.data.detail = detail
          .map((d) => {
            if (typeof d === 'string') return d;
            if (d && typeof d === 'object') {
              const field = Array.isArray(d.loc) ? d.loc.filter((p) => p !== 'body' && p !== 'query').join('.') : '';
              const msg = d.msg || d.message || JSON.stringify(d);
              return field ? `${field}: ${msg}` : msg;
            }
            return String(d);
          })
          .filter(Boolean)
          .join(' · ');
      } else if (typeof detail === 'object') {
        // Single object → best-effort msg extraction
        error.response.data.detail =
          detail.msg || detail.message || JSON.stringify(detail);
      }
    }
    return Promise.reject(error);
  }
);

// ============================================
// Auto-retry on transient errors (GET only)
// ============================================
// Retries idempotent GET requests up to 2 times when we detect a transient
// failure (network error, 502/503/504, or unexpected 500). Exponential
// backoff: 500ms then 1500ms. POST/PUT/DELETE are never retried to avoid
// duplicate writes. SSE stream and login endpoints are excluded.
const MAX_RETRIES = 2;
const RETRY_BACKOFFS_MS = [500, 1500];
const RETRY_STATUSES = new Set([0, 500, 502, 503, 504, 522, 524]);
const NON_RETRYABLE_PATH_FRAGMENTS = ['/api/events/', '/api/auth/login', '/api/auth/register'];

function _shouldRetry(config, error) {
  if (!config || (config.method || 'get').toLowerCase() !== 'get') return false;
  if (config._retryDisabled) return false;
  const url = (config.url || '') + '';
  if (NON_RETRYABLE_PATH_FRAGMENTS.some((f) => url.includes(f))) return false;
  const status = error?.response?.status ?? 0;
  const isNetworkError = !error?.response;
  return isNetworkError || RETRY_STATUSES.has(status);
}

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error?.config;
    if (_shouldRetry(config, error)) {
      config._retryCount = (config._retryCount || 0) + 1;
      if (config._retryCount <= MAX_RETRIES) {
        const delay = RETRY_BACKOFFS_MS[config._retryCount - 1] || 2000;
        await new Promise((r) => setTimeout(r, delay));
        return axios.request(config);
      }
    }
    return Promise.reject(error);
  }
);


const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [sessionId, setSessionId] = useState(localStorage.getItem('session_id'));
  const [loading, setLoading] = useState(true);
  const [sessionExpiresAt, setSessionExpiresAt] = useState(null);

  // Auto-logout function
  const performLogout = useCallback(() => {
    setUser(null);
    setToken(null);
    setSessionId(null);
    setSessionExpiresAt(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('session_id');
    localStorage.removeItem('session_expires_at');
  }, []);

  // Check session expiration
  const checkSessionExpiration = useCallback(() => {
    const expiresAt = localStorage.getItem('session_expires_at');
    if (expiresAt) {
      const expirationTime = new Date(expiresAt).getTime();
      const now = Date.now();
      
      if (now >= expirationTime) {
        // Session expired
        toast.warning('Votre session a expiré. Veuillez vous reconnecter.');
        performLogout();
        return false;
      }
      
      // Warn user 5 minutes before expiration
      const timeRemaining = expirationTime - now;
      if (timeRemaining <= 5 * 60 * 1000 && timeRemaining > 4 * 60 * 1000) {
        toast.info('Votre session expire dans 5 minutes.');
      }
    }
    return true;
  }, [performLogout]);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');
    const storedExpiresAt = localStorage.getItem('session_expires_at');
    
    if (storedUser && storedToken) {
      // Check if session is still valid
      if (storedExpiresAt) {
        const expirationTime = new Date(storedExpiresAt).getTime();
        if (Date.now() >= expirationTime) {
          // Session expired, clear storage
          performLogout();
          setLoading(false);
          return;
        }
      }
      
      setUser(JSON.parse(storedUser));
      setToken(storedToken);
      setSessionId(localStorage.getItem('session_id'));
      setSessionExpiresAt(storedExpiresAt);
    }
    setLoading(false);
  }, [performLogout]);

  // Set up periodic session check (every minute)
  useEffect(() => {
    const interval = setInterval(() => {
      if (user && token) {
        checkSessionExpiration();
      }
    }, 60 * 1000); // Check every minute
    
    return () => clearInterval(interval);
  }, [user, token, checkSessionExpiration]);

  // Set up auto-logout timer
  useEffect(() => {
    if (sessionExpiresAt) {
      const expirationTime = new Date(sessionExpiresAt).getTime();
      const timeUntilExpiration = expirationTime - Date.now();
      
      if (timeUntilExpiration > 0) {
        const timer = setTimeout(() => {
          toast.warning('Votre session a expiré. Veuillez vous reconnecter.');
          performLogout();
        }, timeUntilExpiration);
        
        return () => clearTimeout(timer);
      }
    }
  }, [sessionExpiresAt, performLogout]);

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { username, password });
      const { access_token, user: userData, session_id, expires_in } = response.data;
      
      // Calculate expiration time
      const expiresAt = new Date(Date.now() + (expires_in || SESSION_DURATION_MS / 1000) * 1000);
      
      setUser(userData);
      setToken(access_token);
      setSessionId(session_id);
      setSessionExpiresAt(expiresAt.toISOString());
      
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('token', access_token);
      localStorage.setItem('session_id', session_id || '');
      localStorage.setItem('session_expires_at', expiresAt.toISOString());
      
      return { success: true };
    } catch (error) {
      const statusCode = error.response?.status;
      const detail = error.response?.data?.detail || 'Erreur de connexion';
      
      // Handle locked account (status 423)
      if (statusCode === 423) {
        return { 
          success: false, 
          error: detail,
          locked: true
        };
      }
      
      return { 
        success: false, 
        error: detail 
      };
    }
  };

  const logout = async () => {
    // Notify server about logout
    if (user && sessionId) {
      try {
        await axios.post(`${API}/auth/logout`, null, {
          params: { user_id: user.id, session_id: sessionId }
        });
      } catch (error) {
        console.error('Error notifying server about logout:', error);
      }
    }
    performLogout();
  };

  const checkLoginStatus = async (username) => {
    try {
      const response = await axios.get(`${API}/auth/login-status/${username}`);
      return response.data;
    } catch (error) {
      return { locked: false, remaining_attempts: 5 };
    }
  };

  const getSessionTimeRemaining = () => {
    if (!sessionExpiresAt) return null;
    const expirationTime = new Date(sessionExpiresAt).getTime();
    const remaining = expirationTime - Date.now();
    
    if (remaining <= 0) return 0;
    
    const hours = Math.floor(remaining / (60 * 60 * 1000));
    const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));
    
    return { hours, minutes, totalMs: remaining };
  };

  /**
   * Update selected fields of the current user in-context (and in localStorage)
   * so all mounted components re-render with the new values (e.g. photo_url
   * after profile photo upload). Falls back silently if user not loaded.
   */
  const updateUser = useCallback((patch) => {
    setUser((prev) => {
      if (!prev) return prev;
      const merged = { ...prev, ...(patch || {}) };
      try { localStorage.setItem('user', JSON.stringify(merged)); } catch (_e) { /* ignore quota */ }
      return merged;
    });
  }, []);

  // Subscribe to real-time user-scoped events. When the user updates their
  // photo (or role, or any core field) from another device/tab, propagate
  // it here so all currently-active sessions of the same account stay in
  // sync without needing a page refresh.
  useEventStream({
    token,
    enabled: !!token,
    handlers: {
      user_photo_updated: (data) => {
        if (data?.photo_url !== undefined) updateUser({ photo_url: data.photo_url });
      },
      approval_result: (data) => {
        try {
          const title = data?.title || 'Décision sur votre demande';
          const desc = data?.body || '';
          const url = data?.url;
          toast(title, {
            description: desc,
            duration: 10000,
            action: url ? {
              label: 'Voir',
              onClick: () => { if (url) window.location.href = url; },
            } : undefined,
          });
        } catch (_e) { /* noop */ }
      },
    },
  });

  /**
   * Re-fetch the current user's canonical profile from the backend and refresh
   * every mounted consumer. Useful after any server-side change to the user.
   */
  const refreshUser = useCallback(async () => {
    const uid = user?.id || (() => {
      try { return JSON.parse(localStorage.getItem('user') || '{}').id; } catch (_e) { return null; }
    })();
    if (!uid) return;
    try {
      const { data } = await axios.get(`${API}/users/${uid}/profile`);
      updateUser(data);
    } catch (_e) {
      // ignore transient failures
    }
  }, [user?.id, updateUser]);

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    sessionId,
    sessionExpiresAt,
    checkLoginStatus,
    getSessionTimeRemaining,
    updateUser,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
